import { parseConsentState } from '../../lib/consent/parseConsentState'
import { CONSENT_CONFIG_VERSION, CONSENT_PENDING_COOKIE, hasCurrentConsentEvidence } from '../../lib/consent/backendContract'
import type { ConsentContext, ConsentDecision, ConsentDecisionResult, ConsentEvidence } from '../../lib/consent/backendContract'
import { CONSENT_PURPOSES, CONSENT_REFUSAL_DURATION_MS, CONSENT_COOKIE_NAME } from '../../lib/consent/contract'
import type { ConsentPurpose, ConsentState } from './types'
import { extractCookie, setCookie, deleteCookie } from 'lib/web/cookieUtils'
import { persistConsentState, readConsentState, writeConsentState, continueWithoutOptionalCookies } from './deviceState'
import { clearUngrantedPurposeIdentifiers } from './clearWithdrawnIdentifiers'

const CONSENT_CHECKED_KEY = 'le_consent_checked'

export class ConsentApiError extends Error {
  constructor(public status: number) {
    super('Consent could not be saved')
  }
}

async function request<T>(path: string, method = 'GET', body?: unknown, signal?: AbortSignal): Promise<T> {
  const controller = new AbortController()
  const abort = () => controller.abort()
  if (signal?.aborted) controller.abort()
  signal?.addEventListener('abort', abort, { once: true })
  const timeout = window.setTimeout(abort, 10000)
  try {
    const response = await fetch(`/api/consent/${path}`, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', 'X-LE-Consent': '1' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    if (!response.ok) throw new ConsentApiError(response.status)
    return await response.json()
  } finally {
    window.clearTimeout(timeout)
    signal?.removeEventListener('abort', abort)
  }
}

export async function loadConsentContext(signal?: AbortSignal): Promise<ConsentContext> {
  const context = await request<ConsentContext>('context', 'GET', undefined, signal)
  if (
    context.purposes.length !== CONSENT_PURPOSES.length ||
    CONSENT_PURPOSES.some(
      (key) => context.purposes.filter((purpose) => purpose.key === key && purpose.version).length !== 1,
    )
  ) {
    throw new ConsentApiError(409)
  }
  return context
}

function getBackendConsentState(context: ConsentContext, state = context.state, evidence: ConsentEvidence | null | undefined = window.__LE_CONSENT_BACKEND__, grantedLimit?: Array<ConsentPurpose>): ConsentState | undefined {
  if (!evidence || !state) return undefined
  const consents = context.purposes.map((purpose) =>
    state.consents.find(
      (consent) => consent.purpose_key === purpose.key && consent.purpose_version === purpose.version,
    ),
  )
  if (
    consents.some(
      (consent) =>
        !consent?.last_action ||
        !consent.decided_at ||
        !consent.valid_until ||
        Date.parse(consent.valid_until) <= Date.now(),
    )
  )
  { return undefined }
  const purposes = consents.filter((consent) => consent?.granted && (!grantedLimit || grantedLimit.includes(consent.purpose_key))).map((consent) => consent!.purpose_key)
  const decidedAt = Math.max(...consents.map((consent) => Date.parse(consent!.decided_at!)))
  const expiresAt = Math.min(
    ...consents.map((consent) => Date.parse(consent!.valid_until!)),
    purposes.length ? Infinity : decidedAt + CONSENT_REFUSAL_DURATION_MS,
  )
  if (!Number.isFinite(decidedAt) || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) return undefined
  return {
    version: 1,
    status: purposes.length ? 'accepted' : 'necessary_only',
    purposes,
    decidedAt,
    expiresAt,
    receipt: {
      notice_version: evidence.notice_version,
      vendor_version: evidence.vendor_version,
      config_version: evidence.config_version,
      revision: state.revision,
    },
  }
}

export function cacheBackendConsent(context: ConsentContext, state = context.state, evidence: ConsentEvidence | null | undefined = window.__LE_CONSENT_BACKEND__, grantedLimit?: Array<ConsentPurpose>): ConsentState | undefined {
  const saved = getBackendConsentState(context, state, evidence, grantedLimit)
  if (!saved) return undefined
  const current = readConsentState()
  // Rewriting an unchanged cache would republish consent to GTM and other tabs.
  if (JSON.stringify(current) === JSON.stringify(saved)) return current
  return persistConsentState(saved)
}

export function markConsentChecked(revision: string) {
  try {
    sessionStorage.setItem(CONSENT_CHECKED_KEY, revision)
  } catch {
    // Without session storage the next page load reads the backend again.
  }
}

export function wasConsentCheckedThisSession(state: ConsentState | undefined): boolean {
  try {
    return !!state?.receipt && sessionStorage.getItem(CONSENT_CHECKED_KEY) === state.receipt.revision
  } catch {
    return false
  }
}

function clearConsentChecked() {
  try {
    sessionStorage.removeItem(CONSENT_CHECKED_KEY)
  } catch {
    // An unreadable marker never skips a backend read.
  }
}

export function invalidateBackendConsentCache() {
  clearConsentChecked()
  continueWithoutOptionalCookies()
  clearUngrantedPurposeIdentifiers([])
  try {
    // This is not a saved decision. Its presence keeps manual preview scope on
    // after navigation without ?consentCountry=, including credential expiry.
    setCookie(CONSENT_COOKIE_NAME, 'unconfirmed', CONSENT_REFUSAL_DURATION_MS / 1000, '/', 'Lax')
  } catch {
    // The current page remains denied even when browser storage is unavailable.
  }
}

export function stopWithdrawnPurposes(purposes: Array<ConsentPurpose>) {
  const current = readConsentState()
  const retained = current?.purposes.filter((purpose) => purposes.includes(purpose)) ?? []
  if (current && retained.length < current.purposes.length) {
    // No receipt: this restrictive cache must never be mistaken for a confirmed save.
    if (!writeConsentState(retained.length ? 'accepted' : 'necessary_only', retained)) continueWithoutOptionalCookies()
    clearUngrantedPurposeIdentifiers(retained)
  }
}

export async function createConsentDecision(
  context: ConsentContext,
  purposes: Array<ConsentPurpose>,
  method: ConsentDecision['method'],
  locale: string,
): Promise<ConsentDecision> {
  const evidence = window.__LE_CONSENT_BACKEND__
  if (!evidence?.notice_version || !evidence.vendor_version) throw new ConsentApiError(503)
  const subject = context.state ?? (await request<{ subject_id: string }>('subject', 'POST'))
  return {
    ...evidence,
    subject_id: subject.subject_id,
    revision: context.state?.revision ?? '0',
    idempotency_key: crypto.randomUUID(),
    method,
    language_version: `${locale}:${CONSENT_CONFIG_VERSION}`,
    consents: context.purposes.map((purpose) => ({
      purpose_key: purpose.key,
      purpose_version: purpose.version,
      granted: purposes.includes(purpose.key),
    })),
  }
}

export async function saveBackendConsent(
  context: ConsentContext,
  decision: ConsentDecision,
): Promise<ConsentState | undefined> {
  const result = await request<ConsentDecisionResult>('decision', 'PUT', decision)
  // Read after every write: response ordering must not resurrect a newer refusal.
  const current = await loadConsentContext()
  if (result.subject_id !== decision.subject_id || current.state?.subject_id !== decision.subject_id) throw new ConsentApiError(401)
  if (readPendingDecision()?.idempotency_key !== decision.idempotency_key) throw new ConsentApiError(409)
  const evidence = window.__LE_CONSENT_BACKEND__
  if (!evidence || !hasCurrentConsentEvidence(decision, evidence)) throw new ConsentApiError(409)
  const selected = decision.consents.filter(consent => consent.granted).map(consent => consent.purpose_key)
  const cache = getBackendConsentState(current, current.state, decision, selected)
  if (!cache) throw new ConsentApiError(409)
  clearPendingDecision()
  const saved = persistConsentState(cache)
  if (!saved) {
    storePendingDecision(decision)
    continueWithoutOptionalCookies()
    return undefined
  }
  if (saved.receipt) markConsentChecked(saved.receipt.revision)
  return saved
}

export function readPendingDecision(): ConsentDecision | undefined {
  try {
    const decision = JSON.parse(extractCookie(document.cookie, CONSENT_PENDING_COOKIE) ?? 'null')
    return decision?.idempotency_key && Array.isArray(decision.consents) ? decision : undefined
  } catch {
    return undefined
  }
}

export function storePendingDecision(decision: ConsentDecision): boolean {
  try {
    setCookie(CONSENT_PENDING_COOKIE, JSON.stringify(decision), 365 * 86400, '/', 'Strict')
    return JSON.stringify(readPendingDecision()) === JSON.stringify(decision)
  } catch {
    return false
  }
}

export function clearPendingDecision() {
  deleteCookie(CONSENT_PENDING_COOKIE, '/')
}

export function canReloadAfterFailedWithdrawal(): boolean {
  try {
    const saved = parseConsentState(extractCookie(document.cookie, CONSENT_COOKIE_NAME))
    const evidence = window.__LE_CONSENT_BACKEND__
    return !!extractCookie(document.cookie, CONSENT_PENDING_COOKIE) || !!evidence && !hasCurrentConsentEvidence(saved?.receipt, evidence)
  } catch { return false }
}
